/**
 * Admin Route Component
 *
 * Route guard that redirects unauthorized users:
 * - Unauthenticated users -> /login
 * - Authenticated non-admin users -> /dashboard (with error toast)
 * - Super admin users -> Render children
 *
 * CRITICAL CONSTRAINTS:
 * - Checks user.role from Firestore (via useUserRole hook)
 * - Two-layer security: Client-side redirect (UX) + Server-side validation (actual security)
 * - Shows loading spinner while checking auth and role
 * - TypeScript strict mode (NO 'any' types)
 */

import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useToast } from '@/contexts/ToastContext';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * AdminRoute wrapper for super_admin-only pages
 *
 * Performs two-step authentication:
 * 1. Checks if user is authenticated (via Firebase Auth)
 * 2. Checks if user has SUPER_ADMIN role (via Firestore)
 *
 * @param children - Components to render if user is super_admin
 * @returns Children if authorized, Navigate if not, or loading spinner
 */
export function AdminRoute({ children }: AdminRouteProps): React.JSX.Element {
  const { currentUser, loading: authLoading } = useAuth();
  const { isSuperAdmin, loading: roleLoading } = useUserRole();
  const { showToast } = useToast();

  // Show error toast when user is authenticated but not admin
  useEffect(() => {
    console.log('AdminRoute check:', { authLoading, roleLoading, hasUser: !!currentUser, isSuperAdmin });
    if (!authLoading && !roleLoading && currentUser && !isSuperAdmin) {
      console.log('AdminRoute: Access denied, showing toast');
      showToast('Access denied. Admin privileges required.', 'error', undefined, true);
    }
  }, [authLoading, roleLoading, currentUser, isSuperAdmin, showToast]);

  // Show loading spinner while checking auth and role
  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-green-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Checking admin access...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to dashboard if not super_admin
  // if (!isSuperAdmin) {
  //   return <Navigate to="/dashboard" replace />;
  // }

  // Render admin content
  return <>{children}</>;
}
